import { Puck } from "@measured/puck"
import "@measured/puck/puck.css"
import { Button, message, Modal, Spin } from "antd"

import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import "../config/styles.css"
import { uiBuilderConfig } from "../config/uiBuilderConfig"
import { useUiBuilder } from "../services/ui-builder"
import "./styles.scss"

const getPageTitle = (attributes: any) => {
  return attributes?.isTemplate === true
    ? attributes?.templateName
    : attributes?.page?.data?.attributes?.name ?? attributes?.post?.data?.attributes?.name
}

export function UIBuilder() {
  // 1. Fetch the data from the API /api/block-content/:id, which is id from the URL (params.id)
  // 2. Pass the data to the Puck component
  // 3. When the user clicks on the save button, send the data to the API /api/block-content/:id, which is id from the URL (params.id), the JSON is saved to field `content`
  // 4. When the user clicks on the preview button, redirect the user to /ui-builder

  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [editorData, setEditorData] = useState<unknown>(null)
  const { fetchBlockContent, saveBlockContent, triggerRevalidate } = useUiBuilder()
  const [slugBlockContent, setSlugBlockContent] = useState<string>("")

  const handleSave = async (updatedData: any, isPreview = false) => {
    try {
      await saveBlockContent(id!, updatedData)
      message.success("Lưu thành công nội dung trang.")

      await triggerRevalidate({ name: "pageSlug", value: slugBlockContent })

      // setIsModalVisible(true)
      // if (!isPreview) {
      //   handlePreview()
      // }
    } catch (error) {
      console.error("Error saving data:", error)
    }
  }

  useEffect(() => {
    const disableRootTitle = () => {
      const rootTitleElement = document.querySelector("#root_title") as HTMLInputElement | null
      if (rootTitleElement) {
        rootTitleElement.disabled = true
      }
    }

    disableRootTitle()
  }, [editorData])

  const [isModalVisible, setIsModalVisible] = useState(false)

  const handlePreview = () => {
    // Open new tab with preview
    window.open(`/ui-builder/${id}/preview`, "_blank")
    // navigate(`/ui-builder/${id}/preview`)
  }

  const handleGoBack = () => {
    navigate(`/pages`)
  }

  const handleStay = () => {
    setIsModalVisible(false)
  }

  const DANSPlugin = {
    overrides: {
      // @ts-ignore
      headerActions: ({ children }) => (
        <>
          <Button type="link" variant="link" onClick={handleGoBack}>
            Back to Pages
          </Button>
          {children}
          <Button type="default" color="primary" variant="outlined" onClick={handlePreview}>
            Preview
          </Button>
        </>
      ),
    },
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchBlockContent(id!)
        const pageTitle = getPageTitle(response.data.attributes)
        const content = response.data.attributes.content ? JSON.parse(response.data.attributes.content) : undefined
        const breadcrumbs = content?.root?.props.breadcrumb
        const slugBlockContent = response.data.attributes.page.data?.attributes?.slug
        setSlugBlockContent(slugBlockContent)
        const initialData = {
          ...(content ?? {}),
          root: {
            props: {
              title: pageTitle,
              breadcrumb: breadcrumbs,
            },
          },
        }
        setEditorData(initialData)
      } catch (error) {
        console.error("Error fetching data:", error)
      }
    }

    fetchData()
  }, [id])

  if (!id) {
    navigate(`/`)
  }

  if (!editorData) {
    return <Spin />
  }

  return (
    <div>
      <Puck
        plugins={[DANSPlugin]}
        config={uiBuilderConfig}
        data={editorData}
        onPublish={handleSave}
        viewports={[
          {
            width: 1920,
            height: "auto", // Optional height. Can be numeric or "auto". Defaults to "auto".
            label: "Large Desktop", // Optional. Shown in tooltip.
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-monitor-check"
              >
                <path d="m9 10 2 2 4-4" />
                <rect width="20" height="14" x="2" y="3" rx="2" />
                <path d="M12 17v4" />
                <path d="M8 21h8" />
              </svg>
            ),
          },
          {
            width: 1440,
            height: "auto", // Optional height. Can be numeric or "auto". Defaults to "auto".
            label: "Desktop Macbook 1440", // Optional. Shown in tooltip.
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-laptop-minimal"
              >
                <rect width="18" height="12" x="3" y="4" rx="2" ry="2" />
                <line x1="2" x2="22" y1="20" y2="20" />
              </svg>
            ),
          },
          {
            width: 768,
            height: "auto",
            label: "Tablet",
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-tablet"
              >
                <rect width="16" height="20" x="4" y="2" rx="2" ry="2" />
                <line x1="12" x2="12.01" y1="18" y2="18" />
              </svg>
            ),
          },
          {
            width: 412,
            height: "auto",
            label: "Mobile",
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-smartphone"
              >
                <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
                <path d="M12 18h.01" />
              </svg>
            ), // Optional. Use lucide-icons to align with Puck UI.
          },
        ]}
      />
      <Modal
        centered
        title="Lưu thành công"
        open={isModalVisible}
        onCancel={handleStay}
        footer={[
          <Button type="default" variant="outlined" key="goBack" onClick={handleGoBack}>
            Trở về
          </Button>,
          <Button type="default" variant="outlined" key="stay" onClick={handleStay}>
            Ở lại
          </Button>,
          <Button type="primary" key="preview" onClick={handlePreview}>
            Xem thay đổi
          </Button>,
        ]}
      >
        Bạn có muốn xem các thay đổi?
      </Modal>
    </div>
  )
}
